import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Search, HelpCircle, CreditCard, Video, Users, Shield, Settings } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-gray-800 rounded-lg overflow-hidden">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between text-left bg-gray-900/50 hover:bg-gray-900 transition-colors"
    >
      <span className="font-medium text-white">{question}</span>
      {isOpen ? (
        <ChevronUp className="h-5 w-5 text-purple-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-6 py-4 bg-gray-900/30 text-gray-300 leading-relaxed">
        {answer}
      </div>
    )}
  </div>
);

const VODFAQ = () => {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'getting-started', name: 'Getting Started', icon: Video },
    { id: 'subscription', name: 'Subscription & Billing', icon: CreditCard },
    { id: 'content', name: 'Content & Features', icon: Users },
    { id: 'technical', name: 'Technical Support', icon: Settings },
    { id: 'creators', name: 'For Creators', icon: Shield },
  ];

  const faqs = [
    {
      id: 1,
      category: 'getting-started',
      question: 'What is LTV VOD and how does it work?',
      answer: `LTV VOD is a premium video-on-demand streaming platform that offers exclusive content from talented creators worldwide. Our platform allows you to discover, watch, and support unique entertainment content including shows, documentaries, live events, and short-form videos. Simply create a free account to start exploring our content library, or upgrade to Premium for an ad-free experience with exclusive access to premium content.`
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'How do I create an account on LTV VOD?',
      answer: `Creating an account is quick and easy. Click the "Sign In" button on our homepage and choose to sign up with your email address or continue with Google. If using email, enter your details and verify your email address. Your account will be ready instantly, giving you access to free content and personalized recommendations.`
    },
    {
      id: 3,
      category: 'getting-started',
      question: 'Is LTV VOD available in my country?',
      answer: `LTV VOD is available globally, though some content may have regional restrictions due to licensing agreements. Our platform is accessible from most countries, and we're continuously expanding our content library and availability. If you experience any access issues, please contact our support team for assistance.`
    },
    {
      id: 4,
      category: 'subscription',
      question: 'What are the differences between Free and Premium accounts?',
      answer: `Free accounts provide access to our basic content library with advertisements. Premium subscribers enjoy an ad-free viewing experience, access to exclusive premium content, early access to new releases, higher video quality options (including 4K where available), offline viewing capabilities, and priority customer support. Premium subscriptions can be purchased monthly or annually, with annual plans offering significant savings.`
    },
    {
      id: 5,
      category: 'subscription',
      question: 'How much does a Premium subscription cost?',
      answer: `Our Premium subscription is available at competitive monthly and annual rates. Visit our pricing page or click "Go Premium" for current pricing in your region. We offer a free trial for new subscribers, allowing you to experience premium features before committing. Annual subscriptions provide the best value with up to 20% savings compared to monthly billing.`
    },
    {
      id: 6,
      category: 'subscription',
      question: 'How do I cancel my subscription?',
      answer: `You can cancel your subscription at any time through your Account Settings. Navigate to the Subscription section and click "Cancel Subscription." Your access will continue until the end of your current billing period. We don't offer refunds for partial periods, but you won't be charged again after cancellation. You can re-subscribe at any time to regain premium access.`
    },
    {
      id: 7,
      category: 'subscription',
      question: 'What payment methods do you accept?',
      answer: `We accept all major credit and debit cards including Visa, Mastercard, American Express, and Discover. Payment is processed securely through Stripe, a PCI-compliant payment processor. We also support various regional payment methods depending on your location. All transactions are encrypted and your payment details are never stored on our servers.`
    },
    {
      id: 8,
      category: 'content',
      question: 'What type of content is available on LTV VOD?',
      answer: `LTV VOD features a diverse range of content including original series, documentaries, talk shows, live event recordings, music performances, educational content, and short-form videos (Reels). Our content spans multiple genres including entertainment, lifestyle, culture, music, and more. We work with independent creators and production companies to bring you unique content you won't find elsewhere.`
    },
    {
      id: 9,
      category: 'content',
      question: 'How often is new content added?',
      answer: `We add new content regularly, with fresh videos uploaded daily by our creator community. Major original productions and series are released on scheduled dates, which are announced on our platform and social media channels. Premium subscribers often get early access to new releases before they become available to free users.`
    },
    {
      id: 10,
      category: 'content',
      question: 'Can I download videos for offline viewing?',
      answer: `Premium subscribers can download select videos for offline viewing on mobile devices. Downloaded content is available for a limited time (usually 30 days) and requires periodic online verification. Not all content is available for download due to licensing restrictions. To download, look for the download icon on eligible videos in our mobile app.`
    },
    {
      id: 11,
      category: 'content',
      question: 'What video quality options are available?',
      answer: `LTV VOD offers multiple quality options to accommodate different internet speeds and preferences. Free users can stream in SD and HD quality. Premium subscribers have access to Full HD (1080p) and 4K Ultra HD quality where available. Our adaptive streaming technology automatically adjusts quality based on your connection speed for the smoothest viewing experience.`
    },
    {
      id: 12,
      category: 'technical',
      question: 'What devices can I use to watch LTV VOD?',
      answer: `LTV VOD is accessible on a wide range of devices including desktop browsers (Chrome, Firefox, Safari, Edge), smartphones (iOS and Android), tablets, and smart TVs. We support Chromecast and AirPlay for casting to your TV. For the best experience, we recommend using the latest version of your browser or app.`
    },
    {
      id: 13,
      category: 'technical',
      question: 'Why is my video buffering or not playing?',
      answer: `Buffering issues are typically related to internet connection speed. Try these solutions: check your internet connection, lower the video quality in settings, close other applications using bandwidth, clear your browser cache, try a different browser, or restart your device. If problems persist, contact our support team with details about your device and browser.`
    },
    {
      id: 14,
      category: 'technical',
      question: 'How do I report a technical issue or bug?',
      answer: `To report technical issues, use the "Contact" link in our footer or email support@ltvproduction.com. Please include: a description of the issue, the device and browser you're using, any error messages you see, and steps to reproduce the problem. Screenshots or screen recordings are helpful for our team to diagnose and resolve issues quickly.`
    },
    {
      id: 15,
      category: 'creators',
      question: 'How can I become a content creator on LTV VOD?',
      answer: `We welcome talented creators to join our platform! To become a creator, visit our Creator Portal and submit an application. We review each application to ensure content quality and alignment with our community guidelines. Approved creators get access to our upload tools, analytics dashboard, and monetization features. We support creators with resources and guidance to help them succeed.`
    },
    {
      id: 16,
      category: 'creators',
      question: 'How does creator monetization work?',
      answer: `Creators can earn revenue through multiple streams: ad revenue sharing from views on their content, channel subscriptions from dedicated fans, tips and donations during live streams, and sponsored content opportunities. Earnings are tracked in your Creator Dashboard, and payments are processed monthly once you reach the minimum threshold. Contact our creator support team for detailed information about our revenue sharing structure.`
    },
    {
      id: 17,
      category: 'creators',
      question: 'What are the content guidelines for creators?',
      answer: `All content must comply with our Content Policy, which prohibits: illegal content, explicit adult content, hate speech, harassment, violence, dangerous activities, spam, and misleading content. Creators must own or have proper licenses for all uploaded content. We use a combination of automated systems and human review to enforce guidelines. Violations may result in content removal or account suspension.`
    },
    {
      id: 18,
      category: 'getting-started',
      question: 'How do I reset my password?',
      answer: `To reset your password, click "Sign In" then select "Forgot Password." Enter the email address associated with your account, and we'll send you a password reset link. The link expires after 24 hours for security. If you signed up with Google, you'll need to reset your password through Google's account settings instead.`
    },
    {
      id: 19,
      category: 'subscription',
      question: 'Can I share my account with family members?',
      answer: `Each LTV VOD account is intended for individual use. Sharing login credentials violates our Terms of Service and may result in account suspension. We're working on family plan options that will allow multiple profiles under one subscription. Stay tuned for announcements about family sharing features.`
    },
    {
      id: 20,
      category: 'content',
      question: 'How do I add videos to my watchlist?',
      answer: `To save videos for later, click the bookmark icon on any video thumbnail or detail page. This adds the video to your personal Watchlist, accessible from your account menu. Your Watchlist syncs across all devices, so you can easily continue watching wherever you are. You can also organize your watchlist and remove items at any time.`
    },
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Frequently Asked Questions (FAQ) | LTV VOD - Video Streaming Help</title>
        <meta name="description" content="Find answers to common questions about LTV VOD streaming platform. Learn about subscriptions, content, technical support, creator features, and more." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/faq" />
        <meta property="og:title" content="LTV VOD FAQ - Frequently Asked Questions" />
        <meta property="og:description" content="Get answers to common questions about LTV VOD streaming platform, subscriptions, content, and technical support." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8 brightness-0 invert" />
            <span className="font-bold">LTV VOD</span>
          </Link>
        </div>
      </header>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-purple-900/20 to-transparent py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">How can we help you?</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Find answers to frequently asked questions about LTV VOD
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-900 text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <category.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{category.name}</span>
              </button>
            ))}
          </div>
          
          {/* FAQ List */}
          <div className="space-y-3 max-w-4xl mx-auto">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => (
                <FAQItem
                  key={faq.id}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openItems[faq.id]}
                  onClick={() => toggleItem(faq.id)}
                />
              ))
            ) : (
              <div className="text-center py-12">
                <HelpCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No matching questions found.</p>
                <p className="text-gray-500 text-sm mt-2">Try a different search term or category.</p>
              </div>
            )}
          </div>
          
          {/* Still Need Help */}
          <div className="mt-16 text-center bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-500/20 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
            <p className="text-gray-400 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Link
              to="/vod/contact"
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default VODFAQ;
